import type { RouteSummary } from '@motogo/shared';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type FC,
  type MutableRefObject,
  type ReactNode,
} from 'react';

/**
 * Bridges the discover screen (/discover) and the persistent map — the same data-down
 * pattern as ride review and the route editor. DiscoverPage runs the searches and holds
 * the filters; this provider keeps the results (so navigating into a route detail and
 * back doesn't lose them) and the two map couplings:
 *
 * - `mapCenterRef`: MapView mirrors its center here on every moveend (a ref, no
 *   re-renders) — "Search this area" reads it.
 * - `flyTarget`: set by a place search; MapView eases there. `at` disambiguates
 *   re-selecting the same place.
 */
export interface DiscoverFlyTarget {
  lat: number;
  lon: number;
  /** Target zoom; defaults to a browse-an-area level. */
  zoom?: number;
  at: number;
}

interface DiscoverValue {
  /** DiscoverPage is mounted — MapView draws the result lines while true. */
  active: boolean;
  results: RouteSummary[] | null;
  /** Where the last search ran — anchors the cards' "x km away" labels. */
  searchCenter: { lat: number; lon: number } | null;
  focusedId: string | null;
  flyTarget: DiscoverFlyTarget | null;
  mapCenterRef: MutableRefObject<{ lat: number; lon: number } | null>;
  setActive: (active: boolean) => void;
  setResults: (results: RouteSummary[] | null, center: { lat: number; lon: number } | null) => void;
  setFocusedId: (id: string | null) => void;
  /** Ease the map anywhere — a place-search jump (discover) or a searched waypoint (editor). */
  flyTo: (lat: number, lon: number, zoom?: number) => void;
}

const DiscoverContext = createContext<DiscoverValue | null>(null);

export const DiscoverProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [active, setActive] = useState(false);
  const [results, setResultsState] = useState<RouteSummary[] | null>(null);
  const [searchCenter, setSearchCenter] = useState<{ lat: number; lon: number } | null>(null);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [flyTarget, setFlyTarget] = useState<DiscoverFlyTarget | null>(null);
  const mapCenterRef = useRef<{ lat: number; lon: number } | null>(null);

  const setResults = useCallback((next: RouteSummary[] | null, center: { lat: number; lon: number } | null) => {
    setResultsState(next);
    setSearchCenter(center);
    setFocusedId(null);
  }, []);

  const flyTo = useCallback((lat: number, lon: number, zoom?: number) => {
    setFlyTarget({ lat, lon, zoom, at: Date.now() });
  }, []);

  const value = useMemo<DiscoverValue>(
    () => ({
      active,
      results,
      searchCenter,
      focusedId,
      flyTarget,
      mapCenterRef,
      setActive,
      setResults,
      setFocusedId,
      flyTo,
    }),
    [active, results, searchCenter, focusedId, flyTarget, setResults, flyTo],
  );

  return <DiscoverContext.Provider value={value}>{children}</DiscoverContext.Provider>;
};

export function useDiscover(): DiscoverValue {
  const ctx = useContext(DiscoverContext);
  if (!ctx) {
    throw new Error('useDiscover must be used within a DiscoverProvider');
  }
  return ctx;
}
